import { defineStore } from 'pinia'
import { useClientStore } from './client'

/**
 * Help Panel Store
 * 
 * Manages the state of the interactive help panel that persists across page navigation.
 * Used for displaying help videos and step-by-step guides.
 */
export const useHelpPanelStore = defineStore('helpPanel', {
    state: () => ({
        isOpen: false,
        currentVideo: null,
        currentStepIndex: 0,
        isPlaying: false,
    }),
    
    getters: {
        /**
         * Get the current active step
         */
        currentStep: (state) => {
            if (!state.currentVideo || !state.currentVideo.steps) {
                return null;
            }
            return state.currentVideo.steps[state.currentStepIndex] || null;
        },
        
        /**
         * Check if panel is open
         */
        isPanelOpen: (state) => state.isOpen,
    },
    
    actions: {
        /**
         * Open the help panel with a specific video
         * @param {Object} video - Video data object with title, description, and steps
         */
        openVideo(video) {
            this.currentVideo = video;
            this.currentStepIndex = 0;
            this.isOpen = true;
            this.isPlaying = false;
        },
        
        /**
         * Close the help panel
         */
        closePanel() {
            this.isOpen = false;
            this.currentVideo = null;
            this.currentStepIndex = 0;
            this.isPlaying = false;
        },
        
        /**
         * Set the current step index
         * @param {number} index - Step index (0-based)
         */
        setStep(index) {
            if (this.currentVideo && this.currentVideo.steps && index >= 0 && index < this.currentVideo.steps.length) {
                this.currentStepIndex = index;
            }
        },
        
        /**
         * Navigate to next step
         */
        nextStep() {
            if (this.currentVideo && this.currentVideo.steps) {
                const nextIndex = this.currentStepIndex + 1;
                if (nextIndex < this.currentVideo.steps.length) {
                    this.setStep(nextIndex);
                }
            }
        },
        
        /**
         * Navigate to previous step
         */
        previousStep() {
            if (this.currentStepIndex > 0) {
                this.setStep(this.currentStepIndex - 1);
            }
        },
        
        /**
         * Toggle play/pause state
         */
        togglePlay() {
            this.isPlaying = !this.isPlaying;
        },
    }
})

